import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { DEVICE_WIDTH, previewColors, SYSTEM_FONT_STACK } from './previewTheme'
import type { PreviewContent, TrackerStage } from './types'

const t = buildTranslate('live_updates.preview')

const SHEET_RADIUS = '16px'
const BADGE_SIZE = '28px'
// Vertical connector between timeline steps (Figma 368:7685): a 2px grey50 rule.
const CONNECTOR_HEIGHT = '24px'
const CONNECTOR_WIDTH = '2px'
// A middle row's fixed footprint (badge + connector) — used to reserve space
// for hidden middle rows so toggling a status doesn't resize the sheet.
const MIDDLE_ROW_HEIGHT = parseInt(BADGE_SIZE) + parseInt(CONNECTOR_HEIGHT)
// Summary card + items surface. Duplicates palette.grey05 as a local literal
// rather than the palette token (same rationale as the scene's PAGE_BACKGROUND).
const SURFACE = '#F8F9FA'
const SUMMARY_ICON_SIZE = '36px'

// The pickup (3-stage) and delivery (4-stage) row sets, as stable stage keys —
// mirroring the Live Activity track so the In-App tab reflects delivery-section
// edits too (not just pickup). Each row's default label lives under
// in_app_stages.<stage>; the row matching the focused status shows the
// merchant's edited title instead. Marker icons are decorative.
const PICKUP_STAGES: TrackerStage[] = ['placed', 'preparing', 'ready']
const DELIVERY_STAGES: TrackerStage[] = [
  'placed',
  'preparing',
  'on_the_way',
  'delivered',
]
// FR-03 confirmed-only: only the always-on Placed row remains (no intermediate
// rows, no connector lines).
const CONFIRMED_ONLY_STAGES: TrackerStage[] = ['placed']
const STAGE_ICON: Record<TrackerStage, string> = {
  placed: 'fa fa-cutlery',
  preparing: 'fa fa-clock-o',
  ready: 'fa fa-shopping-bag',
  on_the_way: 'fa fa-truck',
  delivered: 'fa fa-home',
  // Canceled is push-only (no Live Activity / in-app row); never rendered here.
  canceled: 'fa fa-times',
}

export interface InAppTrackerProps {
  content: PreviewContent
  // Whether the group can configure delivery — with a delivery-section focus,
  // switches the row set to the 4-stage delivery list (mirrors LiveActivityCard).
  deliveryCapable?: boolean
  // Tracker stage keys whose statuses are ALL disabled — dropped as a middle
  // row (the first/last row always renders regardless).
  disabledStages?: Set<TrackerStage>
  // Whether the focused configuration's status provider is Flybuy (mirrors
  // StatusCard's FLYBUY_PROVIDER_KEY gate) — shows the Flybuy check-in mock
  // row between the summary card and the stage timeline (Figma 577:7432).
  // Flybuy stacks are expected to never be delivery-capable, but that's a
  // backend-supplied invariant across two independent fields, not something
  // this component can trust blindly — the render guards on isDeliveryContext
  // too, so a future Flybuy+delivery combination hides the row instead of
  // rendering it alongside the delivery track.
  isFlybuyProvider?: boolean
}

// The in-app order-status fly-up sheet (In-App tab). Everything is static mock
// copy except the one active stage row, which shows the merchant's edited
// status title. {location_name} renders literally.
const InAppTracker = ({
  content,
  deliveryCapable = false,
  disabledStages = new Set(),
  isFlybuyProvider = false,
}: InAppTrackerProps) => {
  const [css] = useStyletron()

  const isDeliveryContext = content.section === 'delivery' && deliveryCapable
  const fullStages = isDeliveryContext ? DELIVERY_STAGES : PICKUP_STAGES

  // Confirmed-only (FR-03): every non-'placed' stage is disabled, so only "Order
  // placed" remains. In-app skips directly from Placed to satisfaction — render
  // ONLY the Placed row, with no intermediate rows and no connector lines (this
  // overrides the endpoint-anchoring below, which otherwise keeps Ready /
  // Delivered).
  const confirmedOnly = fullStages.every(
    stage => stage === 'placed' || disabledStages.has(stage)
  )

  // A middle stage whose statuses are all disabled is dropped from the list —
  // the first (Placed) and last (Ready / Delivered) row always render as the
  // list's anchors, regardless of their own enabled state (Figma).
  const stages = confirmedOnly
    ? CONFIRMED_ONLY_STAGES
    : fullStages.filter((stage, index) => {
        const isEndpoint = index === 0 || index === fullStages.length - 1
        return isEndpoint || !disabledStages.has(stage)
      })

  // Reserves the height any hidden middle rows would have taken, as a
  // trailing spacer (rather than inline gaps) so the rendered connector
  // lines stay visually continuous. Assumes a hidden row would have been a
  // single line of label text — a locale where a stage label wraps to 2+
  // lines would make the reservation too short. Skipped for the
  // confirmed-only view (FR-03) via the JSX gate below only, not here, to
  // avoid guarding the same condition twice.
  const hiddenMiddleCount = fullStages.length - stages.length
  const spacerHeight = hiddenMiddleCount * MIDDLE_ROW_HEIGHT

  // A stage outside this track (e.g. a Canceled status, which is push-only and
  // removes the Live Activity per FR-53) highlights no row — rather than falsely
  // pinning the edited copy to the first row.
  const activeIndex = stages.indexOf(content.stage)

  const items = [
    {
      name: t('items.item_a_name'),
      option: t('items.item_a_option'),
      price: t('items.item_a_price'),
    },
    {
      name: t('items.item_b_name'),
      option: null,
      price: t('items.item_b_price'),
    },
  ]

  return (
    <div
      role="group"
      aria-label={t('in_app_frame_label')}
      className={css({
        width: DEVICE_WIDTH,
        maxWidth: '100%',
        borderTopLeftRadius: '24px',
        borderTopRightRadius: '24px',
        border: `8px solid ${previewColors.lightBezel}`,
        borderBottom: 'none',
        backgroundColor: palette.grey10,
        fontFamily: SYSTEM_FONT_STACK,
        paddingTop: '10px',
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      {/* Status bar (light phone → dark glyphs). Decorative. */}
      <div
        aria-hidden="true"
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          height: '20px',
        })}
      >
        <span
          className={css({
            color: palette.grey90,
            fontFamily: SYSTEM_FONT_STACK,
            fontSize: '13px',
            fontWeight: 600,
          })}
        >
          {t('ios_time')}
        </span>
        <div
          className={css({
            display: 'flex',
            alignItems: 'flex-end',
            gap: '2px',
          })}
        >
          {['4px', '6px', '8px', '10px'].map(height => (
            <div
              key={height}
              className={css({
                width: '3px',
                height,
                borderRadius: '1px',
                backgroundColor: palette.grey90,
              })}
            />
          ))}
        </div>
      </div>

      {/* White fly-up sheet */}
      <div
        className={css({
          marginTop: '24px',
          backgroundColor: palette.white,
          borderTopLeftRadius: SHEET_RADIUS,
          borderTopRightRadius: SHEET_RADIUS,
          padding: '20px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        })}
      >
        {/* Header */}
        <div
          className={css({
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          })}
        >
          <div className={css({ display: 'flex', flexDirection: 'column' })}>
            <Text.Header4 tag="h3" bold color="grey90" className="m-0">
              {t('order_status')}
            </Text.Header4>
            <Text.BodyText5
              color="grey70"
              className={css({ display: 'block' })}
            >
              {t('order_number')}
            </Text.BodyText5>
          </div>
          {/* Decorative close affordance — the sheet is a mock, not interactive. */}
          <i
            aria-hidden="true"
            className={`fa fa-times ${css({
              color: palette.grey50,
              fontSize: '18px',
            })}`}
          />
        </div>

        {/* Current-status summary card (Figma 368:7670): a red round icon, the
            merchant's edited status title, and a mock ETA. */}
        <div
          data-testid="lu-in-app-summary"
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '16px',
            borderRadius: '4px',
            backgroundColor: SURFACE,
          })}
        >
          <div
            aria-hidden="true"
            className={css({
              width: SUMMARY_ICON_SIZE,
              height: SUMMARY_ICON_SIZE,
              borderRadius: '50%',
              backgroundColor: previewColors.brandRed,
              color: palette.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            })}
          >
            <i className={`fa fa-cutlery ${css({ fontSize: '16px' })}`} />
          </div>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              flex: 1,
              minWidth: 0,
            })}
          >
            <Text.BodyText3
              bold
              color="grey90"
              className={css({ display: 'block', overflowWrap: 'break-word' })}
            >
              {content.title}
            </Text.BodyText3>
            <Text.BodyText4
              color="grey70"
              className={css({ display: 'block' })}
            >
              {t('in_app_eta')}
            </Text.BodyText4>
          </div>
        </div>

        {/* Flybuy pickup check-in mock (Figma 577:7432): a full-width red
            action row. Rendered unconditionally like every other row in this
            mock — not gated on content.stage — because the reference design
            keeps it visible regardless of which stage is active. Mock chrome
            only, like the rest of this sheet; it isn't wired to a real
            action. Guarded on !isDeliveryContext (see the prop doc above):
            Flybuy is expected to never be delivery-capable, but this keeps
            the row from ever rendering alongside the delivery track if that
            assumption changes. */}
        {isFlybuyProvider && !isDeliveryContext && (
          <div
            data-testid="lu-in-app-check-in"
            className={css({
              padding: '14px',
              borderRadius: '4px',
              backgroundColor: previewColors.brandRed,
              textAlign: 'center',
            })}
          >
            <Text.BodyText3 bold color="white" className="m-0">
              {t('check_in')}
            </Text.BodyText3>
          </div>
        )}

        {/* Vertical stage list */}
        <div
          data-testid="lu-in-app-timeline"
          className={css({ display: 'flex', flexDirection: 'column' })}
        >
          {stages.map((stage, index) => {
            const isActive = index === activeIndex
            const isLast = index === stages.length - 1
            const label = isActive ? content.title : t(`in_app_stages.${stage}`)
            // Off-theme brand red marks the active row (mock chrome); upcoming
            // rows are grey50 (Figma 368:7688/7691).
            const markerColor = isActive
              ? previewColors.brandRed
              : palette.grey50
            const labelColor = isActive
              ? previewColors.brandRed
              : palette.grey50

            return (
              <div
                key={stage}
                className={css({ display: 'flex', gap: '12px' })}
              >
                {/* Marker column: badge + connector to the next row */}
                <div
                  className={css({
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  })}
                >
                  <div
                    aria-hidden="true"
                    className={css({
                      width: BADGE_SIZE,
                      height: BADGE_SIZE,
                      borderRadius: '50%',
                      backgroundColor: markerColor,
                      color: palette.white,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    })}
                  >
                    <i
                      className={`${STAGE_ICON[stage]} ${css({
                        fontSize: '13px',
                      })}`}
                    />
                  </div>
                  {!isLast && (
                    <div
                      aria-hidden="true"
                      className={css({
                        width: CONNECTOR_WIDTH,
                        height: CONNECTOR_HEIGHT,
                        backgroundColor: palette.grey50,
                      })}
                    />
                  )}
                </div>
                {/* Label column */}
                <div
                  className={css({
                    paddingTop: '4px',
                    flex: 1,
                    minWidth: 0,
                  })}
                >
                  <Text.BodyText4
                    bold
                    className={css({
                      display: 'block',
                      color: labelColor,
                      overflowWrap: 'break-word',
                    })}
                  >
                    {label}
                  </Text.BodyText4>
                  {isLast && (
                    <Text.BodyText5
                      color="grey50"
                      className={css({ display: 'block' })}
                    >
                      {t('store_location')}
                    </Text.BodyText5>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {!confirmedOnly && (
          <div
            aria-hidden="true"
            data-testid="lu-in-app-timeline-spacer"
            className={css({ height: `${spacerHeight}px` })}
          />
        )}

        {/* Your items, in a grey surface box (Figma 368:7702). */}
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '16px',
            borderRadius: '4px',
            backgroundColor: SURFACE,
          })}
        >
          <Text.BodyText5
            color="grey50"
            className={css({
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            })}
          >
            {t('your_items')}
          </Text.BodyText5>
          {items.map(item => (
            <div
              key={item.name}
              className={css({
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              })}
            >
              <div
                className={css({ display: 'flex', flexDirection: 'column' })}
              >
                <Text.BodyText4
                  color="grey90"
                  className={css({ display: 'block' })}
                >
                  {item.name}
                </Text.BodyText4>
                {item.option && (
                  <Text.BodyText5
                    color="grey70"
                    className={css({ display: 'block' })}
                  >
                    {item.option}
                  </Text.BodyText5>
                )}
              </div>
              <Text.BodyText4 color="grey90">{item.price}</Text.BodyText4>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default InAppTracker
